import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { DocumentFile, Note, NoteType, Site } from '@shared/types'
import { get } from '../../lib/api'
import { formatDate, formatDateTime, formatTime, localDateKey, pluralise, toDecimalHours } from '../../lib/format'
import { formatBytes } from '../../lib/image'
import { useSession } from '../../state/session'
import { Button, Chip, ErrorNote, Spinner } from '../../components/ui'

interface Report {
  site: Site
  from: string
  to: string
  timezone: string
  notes: Note[]
  documents: DocumentFile[]
  totalMs: number
}

/**
 * The printable site report. A bare page — no app bar, no tabs — so that
 * printing it, or saving it as a PDF from the print dialog, gives a clean
 * document. Specs lead, because they are what somebody reading the report
 * later most often needs to find; everything else follows day by day.
 */
export default function SiteReportPage() {
  const { settings } = useSession()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [report, setReport] = useState<Report | null>(null)
  const [types, setTypes] = useState<NoteType[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([
      get<Report>(`/api/admin/export?${params}`),
      get<{ noteTypes: NoteType[] }>('/api/admin/note-types'),
    ])
      .then(([r, t]) => {
        setReport(r)
        setTypes(t.noteTypes)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not build the report'))
  }, [params])

  const byDay = useMemo(() => {
    const groups = new Map<string, Note[]>()
    for (const note of report?.notes ?? []) {
      if (note.type === 'specs') continue
      const day = localDateKey(new Date(note.occurredAt), report!.timezone)
      groups.set(day, [...(groups.get(day) ?? []), note])
    }
    return [...groups.entries()]
  }, [report])

  if (error) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <ErrorNote>{error}</ErrorNote>
        <Button onClick={() => navigate('/admin/export')}>Back</Button>
      </div>
    )
  }
  if (!report) return <div className="flex min-h-dvh items-center justify-center"><Spinner label="Building the report" /></div>

  const tz = report.timezone
  const specs = report.notes.filter((n) => n.type === 'specs')
  const photoCount = report.notes.reduce((n, note) => n + note.attachments.length, 0)
  const range = report.from === report.to
    ? formatDate(`${report.from}T12:00:00Z`, 'UTC')
    : `${formatDate(`${report.from}T12:00:00Z`, 'UTC')} – ${formatDate(`${report.to}T12:00:00Z`, 'UTC')}`

  return (
    <div className="report mx-auto max-w-3xl bg-raised px-5 py-6 text-graphite">
      <div className="no-print mb-6 flex flex-wrap gap-2">
        <Button onClick={() => navigate('/admin/export')}>‹ Back</Button>
        <Button variant="primary" onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>

      <header className="border-b-2 border-graphite pb-4">
        <p className="label">{settings.orgName} · Site report</p>
        <h1 className="mt-1 text-3xl">{report.site.name}</h1>
        {report.site.address && <p className="text-sm text-graphite-2">{report.site.address}</p>}
        <p className="mt-2 font-mono text-sm tnum">{range}</p>
        <p className="mt-2 text-sm">
          {pluralise(report.notes.length, 'update')} · {pluralise(photoCount, 'photo')} ·{' '}
          {toDecimalHours(report.totalMs)} crew hours on site · {pluralise(report.documents.length, 'document')}
        </p>
        <p className="mt-1 font-mono text-[10px] tracking-wider text-graphite-2 uppercase">
          Generated {formatDateTime(new Date().toISOString(), tz)}
        </p>
      </header>

      {specs.length > 0 && (
        <section className="mt-6">
          <h2 className="report-heading">Specs</h2>
          {specs.map((note) => <ReportNote key={note.id} note={note} types={types} tz={tz} withDate />)}
        </section>
      )}

      <section className="mt-6">
        <h2 className="report-heading">Updates</h2>
        {byDay.length === 0 && <p className="text-sm text-graphite-2">No updates in this range.</p>}
        {byDay.map(([day, notes]) => (
          <div key={day} className="mt-4">
            <h3 className="report-day">{formatDate(`${day}T12:00:00Z`, 'UTC')}</h3>
            {notes.map((note) => <ReportNote key={note.id} note={note} types={types} tz={tz} />)}
          </div>
        ))}
      </section>

      <section className="mt-6">
        <h2 className="report-heading">Documents</h2>
        {report.documents.length === 0 ? (
          <p className="text-sm text-graphite-2">No documents for this site.</p>
        ) : (
          <div className="space-y-3">
            {report.documents.map((doc) => (
              <div key={doc.id} className="report-item">
                <p className="font-bold">{doc.title}</p>
                <p className="text-xs text-graphite-2">
                  {doc.fileName} · {formatBytes(doc.sizeBytes)} · added by {doc.uploadedByName} on{' '}
                  {formatDate(doc.createdAt, tz)}
                </p>
                {/* Pictures print in place; a PDF or Office file cannot be
                    embedded in a printed page, so it is listed with its link. */}
                {doc.contentType.startsWith('image/') && doc.contentType !== 'image/heic' ? (
                  <img src={doc.url} alt={doc.title} className="mt-2 max-h-[22rem] w-auto border border-line" />
                ) : (
                  <a href={doc.url} className="link text-xs break-all">{new URL(doc.url, location.origin).href}</a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function ReportNote({ note, types, tz, withDate }: {
  note: Note
  types: NoteType[]
  tz: string
  withDate?: boolean
}) {
  const type = types.find((t) => t.key === note.type)
  return (
    <article className="report-item mt-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip color={type?.color ?? '#6B6560'}>{type?.label ?? note.type}</Chip>
        <span className="font-mono text-xs tnum">
          {withDate ? formatDateTime(note.occurredAt, tz) : formatTime(note.occurredAt, tz)}
        </span>
        <span className="text-xs font-bold">{note.userName}</span>
      </div>
      {note.body && <p className="mt-1 text-sm whitespace-pre-wrap">{note.body}</p>}
      {note.attachments.length > 0 && (
        <div className="report-photos mt-2">
          {note.attachments.map((a) => (
            <img key={a.id} src={a.url} alt="Site photo" className="w-full border border-line object-cover" />
          ))}
        </div>
      )}
      {note.acknowledgedAt && (
        <p className="mt-1 text-xs text-graphite-2">
          Acknowledged by {note.acknowledgedByName ?? 'the office'}
          {note.ackComment ? `: “${note.ackComment}”` : ''}
        </p>
      )}
    </article>
  )
}
